import { Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import DataObjectLink from "components/atoms/DataObjectLink";
import GoldenNominalBadge from "components/atoms/GoldenNominalBadge";
import SubsumedBadge, { subsumedInto } from "components/atoms/SubsumedBadge";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppSelector } from "hooks";
import React from "react";
import DataTable from "../DataTable";
import HighlightValue from "./HighlightValue";

const { Text } = Typography;

interface IDataObjectSearchResultListProps {
    loading: boolean;
    hits: IDataObjectSearchResultHit[];
    totalHits?: number;
    className?: string;
    onDataObjectSelected?: (d: IDataObjectRecord) => void;
    selectedRowKeys?: string[];
    onPageSelect?: (p: IPagination) => void;
}

export default function DataObjectSearchResultList({
    loading,
    hits,
    totalHits = 0,
    className = "",
    onDataObjectSelected,
    selectedRowKeys = [],
    onPageSelect,
}: IDataObjectSearchResultListProps) {
    const [highlightColumns, setHighlightColumns] = React.useState<{
        tagColumns: string[];
        indexColumnCount: number;
    }>({
        tagColumns: [],
        indexColumnCount: 0,
    });
    const [populatedHits, setPopulatedHists] = React.useState<IDataObjectSearchResultHit[]>([]);
    // Golden status per hit comes from the backend-stamped `_isGoldenNominal` flag, combined with any
    // in-session nominate/revoke override (goldenBySchema) so a change reflects immediately.
    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);

    React.useEffect(() => {
        return () => {
            setHighlightColumns({
                tagColumns: [],
                indexColumnCount: 0,
            });
            setPopulatedHists([]);
        };
    }, []);

    React.useEffect(() => {
        let tagColumns = new Set<string>();
        let indexColumnCount = 0;
        const generatedHits: IDataObjectSearchResultHit[] = [];

        if (hits && hits.length > 0) {
            for (let hit of hits) {
                if (hit.highlight) {
                    const newHit: IDataObjectSearchResultHit = {
                        ...hit,
                    };
                    let hitKeys = Object.keys(hit.highlight);
                    let nonTagColumnCount = 0;

                    // Loop through all keys
                    for (let i = 0; i < hitKeys.length; i++) {
                        // For Array type highlight
                        if (Array.isArray(hit.highlight[hitKeys[i]])) {
                            if (hitKeys[i].startsWith("#")) {
                                // Check if it's tag (tag begins with #)
                                tagColumns.add(hitKeys[i]);
                                newHit.highlight[hitKeys[i]] = hit.highlight[hitKeys[i]];
                            } else {
                                // Non-tag
                                newHit.highlight[nonTagColumnCount] = hit.highlight[hitKeys[i]];
                                nonTagColumnCount = nonTagColumnCount + 1;
                            }
                        } else {
                            // Object: { values:[], tags:[] }
                            const hightlight = hit.highlight[hitKeys[i]] as IHighlightObject;
                            if (hightlight.tags?.length > 0) {
                                for (let tag of hightlight.tags) {
                                    tagColumns.add(tag);
                                    // Present in the tag column
                                    newHit.highlight[tag] = [
                                        {
                                            [hitKeys[i]]: hightlight.values,
                                        },
                                    ];
                                }
                            } else {
                                newHit.highlight[nonTagColumnCount] = [
                                    {
                                        [hitKeys[i]]: hightlight.values,
                                    },
                                ];
                                nonTagColumnCount = nonTagColumnCount + 1;
                            }
                        }
                    }

                    indexColumnCount = Math.max(indexColumnCount, nonTagColumnCount);
                    generatedHits.push(newHit);
                } else {
                    // No highlight
                    generatedHits.push({ ...hit });
                }
            }
        }
        setHighlightColumns({
            tagColumns: Array.from(tagColumns),
            indexColumnCount,
        });
        setPopulatedHists(generatedHits);
    }, [hits]);

    const onClickRow = (record: IDataObjectSearchResultHit) => {
        return {
            onClick: () => {
                onDataObjectSelected && onDataObjectSelected(record.item);
            },
        };
    };

    const taggedColumns: ColumnsType<IDataObjectSearchResultHit> = React.useMemo(
        () =>
            highlightColumns.tagColumns.map((tagColumn) => ({
                dataIndex: ["highlight", tagColumn],
                title: <Tag>{tagColumn}</Tag>,
                className: "min-w-52",
                shouldCellUpdate: (record, prevRecord) => {
                    return record !== prevRecord;
                },
                render: (value, hit, index) => {
                    if (value) {
                        return <HighlightValue highlightValues={value}></HighlightValue>;
                    }
                    return <></>;
                },
            })),
        [highlightColumns.tagColumns],
    );

    const highlightedColumns: ColumnsType<IDataObjectSearchResultHit> = React.useMemo(
        () =>
            [...Array(highlightColumns.indexColumnCount)].map((item, index) => ({
                className: "min-w-52",
                dataIndex: ["highlight", index],
                title: <Text type="secondary" className="whitespace-nowrap">{`Untagged Field(${index + 1})`}</Text>,
                shouldCellUpdate: (record, prevRecord) => {
                    return record !== prevRecord;
                },
                render: (value, hit, index) => {
                    if (value) {
                        return <HighlightValue highlightValues={value}></HighlightValue>;
                    }
                    return <></>;
                },
            })) || [],
        [highlightColumns.indexColumnCount],
    );

    return (
        <DataTable<IDataObjectSearchResultHit>
            dataTestId="data-object-search-results"
            hideSelectionColumn
            emptyText={loading ? "" : "No result found"}
            tableProps={{
                dataSource: populatedHits,
                rowKey: (hit) => hit.item._dataObjectId,
                loading: loading ? { size: "large", tip: "Getting search result..." } : false,
                className: className,
                rowClassName: "cursor-pointer",
                onRow: (r) => onClickRow(r),
                pagination: {
                    total: totalHits,
                },
                onChange: (pagination, _filters, _sorter, extra) => extra.action === "paginate" && onPageSelect && onPageSelect(pagination as IPagination),
                rowSelection: {
                    selectedRowKeys,
                },
                columns: [
                    {
                        key: "_businessAreaId",
                        dataIndex: ["item", "_businessAreaName"],
                        title: "Business Area",
                        ellipsis: {
                            showTitle: false,
                        },
                        shouldCellUpdate: (record, prevRecord) => {
                            return record !== prevRecord;
                        },
                        render: (value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                    {
                        key: "_schemaId",
                        dataIndex: ["item", "_schemaName"],
                        title: "Schema",
                        ellipsis: {
                            showTitle: false,
                        },
                        shouldCellUpdate: (record, prevRecord) => {
                            return record !== prevRecord;
                        },
                        render: (value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        ),
                    },
                    {
                        key: "_dataSetId",
                        dataIndex: ["item", "_dataSetName"],
                        title: "Dataset",
                        ellipsis: {
                            showTitle: false,
                        },
                        shouldCellUpdate: (record, prevRecord) => {
                            return record !== prevRecord;
                        },
                        render: (value, hit, index) => (
                            <span className="inline-flex items-center gap-2">
                                <Tooltip placement="topLeft" title={value}>
                                    <span className="truncate">{value}</span>
                                </Tooltip>
                                {isDatasetGolden(goldenBySchema, hit?.item?._schemaId, hit?.item?._dataSetId, hit?.item?._isGoldenNominal) && <GoldenNominalBadge label="GOLDEN" />}
                                {subsumedInto(hit?.item) !== null && <SubsumedBadge survivorId={subsumedInto(hit?.item)} />}
                            </span>
                        ),
                    },
                    {
                        key: "_dataObjectId",
                        dataIndex: ["item", "_dataObjectId"],
                        title: "RecordID",
                        ellipsis: {
                            showTitle: false,
                        },
                        shouldCellUpdate: (record, prevRecord) => {
                            return record !== prevRecord;
                        },
                        render: (value, hit, index) => <DataObjectLink dataObjectId={value}></DataObjectLink>,
                    },
                    ...taggedColumns,
                    ...highlightedColumns,
                ],
            }}
        />
    );
}
